import React from "react";
import { Stack } from "react-bootstrap";
import { useTranslation } from "react-i18next";

import "./HprBalanceBreakdown.scss";
import BottomDrawer from "../../BottomDrawer/BottomDrawer";
import { GenericModalProps } from "../../../utils/constants/generic-types";
import PrimaryActionButton from "../../Buttons/PrimaryActionButton/PrimaryActionButton";
import WarningBadge from "../../WarningBadge/WarningBadge";
import HyperCoin from "../../HyperCoin/HyperCoin";
import { formatHprBalance } from "../../../utils/helpers";
import useHprFiatFormatter from "../../../hooks/useHprFiatFormatter";

interface HprBalanceItemProps {
  title: string;
  hprAmount: number;
  isFiat?: boolean;
  formatHprToFiatValue: (amount?: number) => string;
}

interface HprBalanceBreakdownProps extends GenericModalProps {
  totalHprBalance?: number;
  realHprBalance?: number;
  bonusHprBalance?: number;
}

function HprBalanceItem({
  title,
  hprAmount,
  isFiat,
  formatHprToFiatValue,
}: HprBalanceItemProps) {
  return (
    <div className="d-flex justify-content-between HprBalanceItem">
      <p className="Grotesk HprBalanceTitle">{title}</p>
      <p className="d-flex align-items-center Grotesk HprAmount">
        {!isFiat && <HyperCoin width={13.3} height={13.3} />}
        <span>
          {isFiat
            ? formatHprToFiatValue(hprAmount)
            : formatHprBalance(hprAmount)}
        </span>
      </p>
    </div>
  );
}

export default function HprBalanceBreakdown({
  isOpen,
  onClose,
  totalHprBalance = 0,
  realHprBalance = 0,
  bonusHprBalance = 0,
}: HprBalanceBreakdownProps) {
  const { t } = useTranslation(["games", "common"]);
  const { isFiatDisplay, formatHprToFiatValue } = useHprFiatFormatter();

  return (
    <BottomDrawer
      isOpen={isOpen}
      onClose={onClose}
      showCloseBtn={false}
      containerClass="HprBalanceBreakdownModal"
    >
      <Stack className="HprBalanceBreakdownContent">
        <Stack gap={4}>
          <h2 className="text-center Hanson-700 ModalTitle">
            Balance Breakdown
          </h2>
          <Stack gap={3}>
            <HprBalanceItem
              title={
                isFiatDisplay
                  ? t("balance_breakdown.total_balance")
                  : t("balance_breakdown.total_hpr")
              }
              hprAmount={totalHprBalance}
              isFiat={isFiatDisplay}
              formatHprToFiatValue={formatHprToFiatValue}
            />
            <HprBalanceItem
              title={t("balance_breakdown.available_for_cashout")}
              hprAmount={realHprBalance}
              isFiat={isFiatDisplay}
              formatHprToFiatValue={formatHprToFiatValue}
            />
            <HprBalanceItem
              title={
                isFiatDisplay
                  ? t("balance_breakdown.bonus")
                  : t("balance_breakdown.bonus_hpr")
              }
              hprAmount={bonusHprBalance}
              isFiat={isFiatDisplay}
              formatHprToFiatValue={formatHprToFiatValue}
            />
            <WarningBadge
              iconSize={16}
              textClassName="BonusHprInfoText"
              containerClassName="BonusHprInfo"
              text={
                isFiatDisplay
                  ? t("balance_breakdown.warning_fiat")
                  : t("balance_breakdown.warning_hpr")
              }
            />
          </Stack>
        </Stack>

        <PrimaryActionButton text={t("common:got_it")} onClick={onClose} />
      </Stack>
    </BottomDrawer>
  );
}
